import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2, Users, X } from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  bulkUpsertAssignments,
  getAgencyClinics,
  getAgencyMembers,
  getUsersByRole,
} from '../../services/api';
import type {
  AgencyClinicResponse,
  RoleResponse,
} from '../../services/api/types.gen';

type AgencyUser = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
};

type Props = {
  role: RoleResponse;
  open: boolean;
  onClose: () => void;
};

const ALL_CLINICS_VALUE = '__all_clinics__';

const BulkAssignRoleDialog: React.FC<Props> = ({ role, open, onClose }) => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const isClinicScoped = role.scope.toLowerCase() === 'clinic';

  const [users, setUsers] = useState<AgencyUser[]>([]);
  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([]);
  const [allClinicsSelected, setAllClinicsSelected] = useState(false);

  // user_id → clinic_ids they already have this role at (from the API)
  const [existingUserClinicIds, setExistingUserClinicIds] = useState<
    Map<string, string[]>
  >(new Map());

  const [userSearch, setUserSearch] = useState('');
  const [clinicSearch, setClinicSearch] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [clinicDropdownOpen, setClinicDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const userDropdownRef = useRef<HTMLDivElement>(null);
  const clinicDropdownRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!open) return;

    setIsLoadingUsers(true);
    let loadedClinics: AgencyClinicResponse[] = [];

    if (isClinicScoped) {
      setIsLoadingClinics(true);
      try {
        const res = await getAgencyClinics({});
        // EMR-375: every clinic is Roger-assignable.
        loadedClinics = res.data ?? [];
        setClinics(loadedClinics);
      } catch (error) {
        console.error('Failed to load clinics:', error);
        showError(
          'Failed to Load Clinics',
          'Unable to load clinics. Please try again.'
        );
      } finally {
        setIsLoadingClinics(false);
      }
    }

    try {
      const [usersRes, existingRes] = await Promise.all([
        getAgencyMembers({}),
        getUsersByRole({
          path: { role_id: role.id },
          query: isClinicScoped
            ? { clinic_ids: loadedClinics.map(c => c.id) }
            : undefined,
        }),
      ]);

      const data = (usersRes.data ?? []) as AgencyUser[];
      setUsers(data);

      const existingItems = existingRes.data ?? [];
      const existingIds = existingItems.map(item => item.user_id);
      setSelectedUserIds(existingIds);

      if (isClinicScoped) {
        const map = new Map<string, string[]>();
        for (const item of existingItems) {
          map.set(item.user_id, item.clinic_ids ?? []);
        }
        setExistingUserClinicIds(map);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      showError(
        'Failed to Load Users',
        'Unable to load users. Please try again.'
      );
    } finally {
      setIsLoadingUsers(false);
    }
  }, [open, isClinicScoped, role.id, showError]);

  useEffect(() => {
    if (open) {
      setSelectedUserIds([]);
      setSelectedClinicIds([]);
      setAllClinicsSelected(false);
      setExistingUserClinicIds(new Map());
      setUserSearch('');
      setClinicSearch('');
      load();
    }
  }, [open, load]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(e.target as Node)
      ) {
        setUserDropdownOpen(false);
      }
      if (
        clinicDropdownRef.current &&
        !clinicDropdownRef.current.contains(e.target as Node)
      ) {
        setClinicDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const filteredUsers = users.filter(u => {
    const full = `${u.first_name} ${u.last_name}`.toLowerCase();
    return full.includes(userSearch.toLowerCase());
  });

  const filteredClinics = clinics.filter(c =>
    c.name.toLowerCase().includes(clinicSearch.toLowerCase())
  );

  const toggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const removeUser = (userId: string) => {
    setSelectedUserIds(prev => prev.filter(id => id !== userId));
  };

  const toggleClinic = (clinicId: string) => {
    if (clinicId === ALL_CLINICS_VALUE) {
      const newAllSelected = !allClinicsSelected;
      setAllClinicsSelected(newAllSelected);
      setSelectedClinicIds(newAllSelected ? clinics.map(c => c.id) : []);
      return;
    }
    setAllClinicsSelected(false);
    setSelectedClinicIds(prev =>
      prev.includes(clinicId)
        ? prev.filter(id => id !== clinicId)
        : [...prev, clinicId]
    );
  };

  const removeClinic = (clinicId: string) => {
    setSelectedClinicIds(prev => prev.filter(id => id !== clinicId));
    setAllClinicsSelected(false);
  };

  const handleSubmit = async () => {
    if (selectedUserIds.length === 0) {
      showError(
        'No Users Selected',
        'Please select at least one user to assign.'
      );
      return;
    }
    if (isClinicScoped && selectedClinicIds.length === 0) {
      showError(
        'No Clinics Selected',
        'Please select at least one clinic for this clinic-scoped role.'
      );
      return;
    }

    setIsSaving(true);
    try {
      await bulkUpsertAssignments({
        body: {
          role_id: role.id,
          user_ids: selectedUserIds,
          clinic_ids: isClinicScoped ? selectedClinicIds : [],
        },
      });
      showSuccess(`${role.name} updated`, 'Role assignments have been saved.');
      onClose();
    } catch (error) {
      console.error('Failed to bulk assign role:', error);
      showError(
        'Assignment Failed',
        'Unable to assign the role. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const selectedUsers = users.filter(u => selectedUserIds.includes(u.id));
  const selectedClinicObjects = clinics.filter(c =>
    selectedClinicIds.includes(c.id)
  );

  const clinicTriggerLabel = () => {
    if (selectedClinicIds.length === 0) return 'Select clinics...';
    if (allClinicsSelected) return 'All clinics';
    if (selectedClinicIds.length === 1)
      return selectedClinicObjects[0]?.name ?? '1 clinic';
    return `${selectedClinicIds.length} clinics selected`;
  };

  const getClinicNamesForUser = (userId: string): string => {
    const ids = existingUserClinicIds.get(userId) ?? [];
    if (ids.length === 0) return '';
    const names = ids
      .map(id => clinics.find(c => c.id === id)?.name ?? id)
      .join(', ');
    return names;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Users className='h-5 w-5' />
            Bulk Assign Role
          </DialogTitle>
        </DialogHeader>

        {/* Role summary */}
        <div className='rounded-md border bg-muted/40 p-3 space-y-1.5'>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='font-medium text-sm'>{role.name}</span>
            <Badge variant='outline' className='capitalize text-xs'>
              {role.scope.toLowerCase()}
            </Badge>
          </div>
          {role.description && (
            <p className='text-xs text-muted-foreground'>{role.description}</p>
          )}
          {(role.permissions ?? []).length > 0 && (
            <div className='flex flex-wrap gap-1 pt-0.5'>
              {(role.permissions ?? []).map(p => (
                <Badge key={p.id} variant='outline' className='text-xs'>
                  {p.name}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className='space-y-4'>
          {/* Clinic selector — only for clinic-scoped roles */}
          {isClinicScoped && (
            <div className='space-y-1.5'>
              <Label className='text-sm font-medium'>Clinics</Label>

              {/* Clinic dropdown */}
              <div ref={clinicDropdownRef} className='relative'>
                <Button
                  variant='outline'
                  className='w-full justify-between font-normal'
                  onClick={() => setClinicDropdownOpen(p => !p)}
                  disabled={isLoadingClinics}
                  type='button'
                >
                  <span className='text-sm truncate'>
                    {isLoadingClinics
                      ? 'Loading clinics...'
                      : clinicTriggerLabel()}
                  </span>
                  <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
                </Button>

                {clinicDropdownOpen && !isLoadingClinics && (
                  <div className='absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md'>
                    <div className='p-2 border-b'>
                      <Input
                        placeholder='Search clinics...'
                        value={clinicSearch}
                        onChange={e => setClinicSearch(e.target.value)}
                        className='h-8 text-sm'
                        autoFocus
                      />
                    </div>
                    <div className='max-h-48 overflow-y-auto p-1'>
                      {/* Div row: Radix Checkbox renders as <button>; avoid nested <button> inside <button>. */}
                      <div
                        className='flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer'
                        onClick={() => toggleClinic(ALL_CLINICS_VALUE)}
                      >
                        <Checkbox
                          checked={allClinicsSelected}
                          id='all-clinics'
                          onCheckedChange={() =>
                            toggleClinic(ALL_CLINICS_VALUE)
                          }
                          onClick={e => e.stopPropagation()}
                        />
                        <span className='font-medium'>All current clinics</span>
                      </div>
                      {filteredClinics.length === 0 ? (
                        <p className='py-2 text-center text-xs text-muted-foreground'>
                          No clinics found.
                        </p>
                      ) : (
                        filteredClinics.map(clinic => (
                          <div
                            key={clinic.id}
                            className='flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer'
                            onClick={() => toggleClinic(clinic.id)}
                          >
                            <Checkbox
                              checked={selectedClinicIds.includes(clinic.id)}
                              id={`clinic-${clinic.id}`}
                              onCheckedChange={() => toggleClinic(clinic.id)}
                              onClick={e => e.stopPropagation()}
                            />
                            <span className='truncate'>{clinic.name}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Selected clinic tags */}
              {!allClinicsSelected && selectedClinicObjects.length > 0 && (
                <div className='flex flex-wrap gap-1.5 pt-1'>
                  {selectedClinicObjects.map(c => (
                    <span
                      key={c.id}
                      className='inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground'
                    >
                      {c.name}
                      <button
                        type='button'
                        onClick={() => removeClinic(c.id)}
                        className='ml-0.5 hover:text-destructive'
                        aria-label={`Remove ${c.name}`}
                      >
                        <X className='h-3 w-3' />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {allClinicsSelected && (
                <p className='text-xs text-muted-foreground pt-0.5'>
                  All {clinics.length} clinic{clinics.length !== 1 ? 's' : ''}{' '}
                  will be included.
                </p>
              )}
            </div>
          )}

          {/* User selector */}
          <div className='space-y-1.5'>
            <Label className='text-sm font-medium'>Users</Label>

            <div ref={userDropdownRef} className='relative'>
              <Button
                variant='outline'
                className='w-full justify-between font-normal'
                onClick={() => setUserDropdownOpen(p => !p)}
                disabled={isLoadingUsers}
                type='button'
              >
                <span className='text-sm text-muted-foreground'>
                  {isLoadingUsers
                    ? 'Loading users...'
                    : selectedUserIds.length === 0
                      ? 'Select users...'
                      : `${selectedUserIds.length} user${selectedUserIds.length !== 1 ? 's' : ''} selected`}
                </span>
                <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
              </Button>

              {userDropdownOpen && !isLoadingUsers && (
                <div className='absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md'>
                  <div className='p-2 border-b'>
                    <Input
                      placeholder='Search by name...'
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      className='h-8 text-sm'
                      autoFocus
                    />
                  </div>
                  <div className='max-h-52 overflow-y-auto p-1'>
                    {filteredUsers.length === 0 ? (
                      <p className='py-2 text-center text-xs text-muted-foreground'>
                        No users found.
                      </p>
                    ) : (
                      filteredUsers.map(user => {
                        const fullName =
                          `${user.first_name} ${user.last_name}`.trim() ||
                          user.email;
                        return (
                          <div
                            key={user.id}
                            className='flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer'
                            onClick={() => toggleUser(user.id)}
                          >
                            <Checkbox
                              checked={selectedUserIds.includes(user.id)}
                              id={`user-${user.id}`}
                              onCheckedChange={() => toggleUser(user.id)}
                              onClick={e => e.stopPropagation()}
                            />
                            <span className='truncate'>{fullName}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Selected user tags */}
            {selectedUsers.length > 0 && (
              <div className='flex flex-wrap gap-1.5 pt-1'>
                {selectedUsers.map(u => {
                  const fullName =
                    `${u.first_name} ${u.last_name}`.trim() || u.email;
                  const clinicNames = isClinicScoped
                    ? getClinicNamesForUser(u.id)
                    : '';
                  return (
                    <span
                      key={u.id}
                      title={clinicNames || undefined}
                      className='inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground'
                    >
                      {fullName}
                      <button
                        type='button'
                        onClick={() => removeUser(u.id)}
                        className='ml-0.5 hover:text-destructive'
                        aria-label={`Remove ${fullName}`}
                      >
                        <X className='h-3 w-3' />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Assigning...
              </>
            ) : (
              'Assign Role'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkAssignRoleDialog;
